import { Box, Stack } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

export interface FileUploadShellRootProps {
  isDragActive: boolean;
  /**
   * The drag in progress carries something the zone will REFUSE.
   *
   * Only meaningful together with `isDragActive`: it is the drag-time half of the
   * refusal, and without it a dragged XLSX lit the zone in the same primary blue as
   * a PDF — an affirmative "drop it here" issued a moment before nothing happened.
   *
   * It is a HINT, not a verdict. `dragover` exposes MIME types and no file names, so
   * a file the browser types as `''` reads as acceptable here and is still refused on
   * drop. The message the shell renders after the drop is the authority.
   */
  isDragReject?: boolean;
  hasLibrarySection?: boolean;
  /** The whole area opens the file picker, so it must read as a target. */
  isClickable?: boolean;
}

/** Outer dashed drop area aligned with the request file-upload block design. */
export const FileUploadShellRoot = styled(Box, {
  shouldForwardProp: (p) =>
    p !== 'isDragActive' &&
    p !== 'isDragReject' &&
    p !== 'hasLibrarySection' &&
    p !== 'isClickable',
})<FileUploadShellRootProps>(({
  theme,
  isDragActive,
  isDragReject = false,
  hasLibrarySection,
  isClickable,
}) => {
  /* Three states, one accent, and the refusal has to win: a rejected drag is still
     an ACTIVE one, so reading `isDragActive` first would paint the encouraging
     colour over the discouraging one. */
  const accent = isDragReject ? theme.palette.error.main : theme.palette.primary.main;

  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: isClickable ? 'pointer' : 'default',
    padding: theme.spacing(3),
    borderRadius: hasLibrarySection ? '8px 8px 0 0' : 8,
    border: `1px dashed ${isDragActive ? accent : theme.palette.grey[100]}`,
    backgroundColor: isDragActive ? alpha(accent, 0.08) : theme.palette.grey[120],
    transition: 'border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease',
    outline: 'none',
    marginBottom: hasLibrarySection ? -1 : 0,
    '&:focus-visible': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.32)}`,
    },
    '&:hover': {
      borderColor: isDragActive ? accent : alpha(theme.palette.primary.main, 0.55),
    },
  };
});

export const FileUploadIconTile = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: theme.palette.primary.main,
}));

/** Search field for audit / library documents (sits below the dashed dropzone). */
export const FileUploadLibraryField = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  border: `1px dashed ${theme.palette.grey[100]}`,
  borderRadius: '0 0 8px 8px',
  backgroundColor: theme.palette.background.paper,
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    minHeight: 40,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    '& fieldset': {
      borderColor: theme.palette.grey[100],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.grey[80],
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.18)}`,
    },
  },
  '& .MuiInputBase-input': {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.6,
    padding: '0 !important',
    color: theme.palette.text.primary,
    '&::placeholder': {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
  '& .MuiInputAdornment-root': {
    color: theme.palette.grey[80],
    marginRight: theme.spacing(1),
  },
}));

export const FileUploadAttachmentCard = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  backgroundColor: theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.18s ease, opacity 0.18s ease, box-shadow 0.18s ease',
}));

export const FileUploadProgressBar = styled(Box)(({ theme }) => ({
  width: '100%',
  paddingTop: theme.spacing(0.25),
  '& .MuiLinearProgress-root': {
    height: 4,
    borderRadius: 999,
    backgroundColor: theme.palette.grey[100],
  },
  '& .MuiLinearProgress-bar': {
    borderRadius: 999,
    background: `linear-gradient(90deg, ${theme.palette.success.light} 0%, ${theme.palette.success.light} 50%, ${theme.palette.success.main} 100%)`,
    transition: 'transform 0.24s ease',
  },
}));

/**
 * Heading over a run of library rows the picker offers but will not take.
 *
 * MUI draws its own group label through a styled component whose class carries no
 * standalone styling, so a custom `renderGroup` — which is what lets the SUPPORTED
 * half render with no heading at all — has to bring the label's look with it.
 */
export const FileUploadLibraryGroupLabel = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: -8,
  zIndex: 1,
  padding: theme.spacing(1, 2),
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
}));

/** The `<ul>` MUI's own `renderGroup` wraps a group's options in. */
export const FileUploadLibraryGroupList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
});
